import { ISourceRepositorySettings, SourceRepositoryProvider } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { ProjectSettingsService } from "../../../../services/project-settings-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import useLoadingPresentation, { LoadingPresentation } from "../../../../shared/hooks/useLoadingPresentation";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { DeleteModal } from "../../../common/delete-modal";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { KuikaSkeletonForm, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import "./project-settings-container.scss";

const TOKEN_MASK: string = "********";

const PROVIDER_OPTIONS: KuikaSelectboxOption[] = [
  { value: "github", label: "GitHub" },
  { value: "azureDevOps", label: "Azure DevOps (TFS)" }
];

interface IApiErrorResponseData {
  message?: string;
}

interface IApiErrorResponse {
  data?: IApiErrorResponseData | string;
}

interface IApiErrorShape {
  response?: IApiErrorResponse;
}

const extractErrorMessage = (error: unknown, fallback: string): string => {
  const shaped: IApiErrorShape = error as IApiErrorShape;
  const data: IApiErrorResponseData | string | undefined = shaped?.response?.data;
  if (typeof data === "string" && data.length > 0) return data;
  if (data && typeof data === "object" && typeof data.message === "string") return data.message;
  return fallback;
};

interface ISourceRepositoryFormState {
  provider: SourceRepositoryProvider;
  url: string;
  owner: string;
  tfsProject: string;
  user: string;
  token: string;
  branch: string;
  useAutoGeneratedBranch: boolean;
  repoFrontend: string;
  repoBackend: string;
  repoWorkflowEngine: string;
  repoAndroid: string;
  repoIos: string;
}

const EMPTY_FORM: ISourceRepositoryFormState = {
  provider: "github",
  url: "",
  owner: "",
  tfsProject: "",
  user: "",
  token: "",
  branch: "",
  useAutoGeneratedBranch: false,
  repoFrontend: "",
  repoBackend: "",
  repoWorkflowEngine: "",
  repoAndroid: "",
  repoIos: ""
};

export interface IProjectSettingsContainerProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Project-level settings, as a dialog over the Config Manager.
 *
 * It owns its own sheet rather than being poured into one, so its commit actions can sit in
 * the dialog's real footer — outside the scroll, where a long form's Save belongs — without
 * the host having to hold this component's saving state for it.
 *
 * Source Repository is the only section. A second one makes the body a `KuikaTabs` inside
 * the same dialog; a single tab strip over a single section is noise.
 *
 * Document parameters briefly lived here as a second tab and moved out to their own top-level tool: this
 * dialog's entry point is gated on the source-repository opt-in, which left the other tab unreachable in a
 * default deployment. If a genuinely ungated section is added here, lift that gate off the entry point first.
 */
const ProjectSettingsContainerInner: FunctionComponent<IProjectSettingsContainerProps & IWithSDProps> = ({
  sd,
  visible,
  onClose
}: IProjectSettingsContainerProps & IWithSDProps) => {
  const projectId: string = sd.project?.id ?? "";
  const projectName: string = sd.project?.name ?? "";

  const [form, setForm] = useState<ISourceRepositoryFormState>(EMPTY_FORM);
  const [hasExisting, setHasExisting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  // Save stays in the open dialog, so the button is its confirmation: "Saving", then "Saved"
  // held long enough to read, then Save again. `SaveStatus` owns that order; a function
  // component keeps the one machine for its lifetime instead of writing a second.
  const [saveStatus, setSaveStatus] = useState<KuikaButtonStatus>("idle");
  const [saveMachine] = useState<SaveStatus>((): SaveStatus => new SaveStatus(setSaveStatus));

  useEffect((): (() => void) => (): void => saveMachine.dispose(), [saveMachine]);

  // Every opening re-reads the settings, and the fields still hold the last opening's values until
  // it answers — so there is never data of its own to keep on screen: the form's outline instead,
  // after the usual grace period, and the fields once they are this opening's.
  const presentation: LoadingPresentation = useLoadingPresentation({ isLoading, hasData: false });
  const isFormWaiting: boolean = presentation === "pending" || presentation === "skeleton";

  // Re-read on every open as well as on a project change: the sheet stays mounted between
  // openings now that it owns itself, so without `visible` in the deps a second opening
  // would show whatever was on screen when it was closed.
  useEffect(() => {
    if (!projectId || !visible) return;
    setIsLoading(true);
    ProjectSettingsService.getProjectSettings(projectId)
      .then((response) => {
        const settings: ISourceRepositorySettings | null | undefined = response.data?.sourceRepository;
        if (settings) {
          setForm({
            provider: settings.provider ?? "github",
            url: settings.url ?? "",
            owner: settings.owner ?? "",
            tfsProject: settings.tfsProject ?? "",
            user: settings.user ?? "",
            token: "",
            branch: settings.branch ?? "",
            useAutoGeneratedBranch: settings.useAutoGeneratedBranch ?? false,
            repoFrontend: settings.repositories?.frontend ?? "",
            repoBackend: settings.repositories?.backend ?? "",
            repoWorkflowEngine: settings.repositories?.workflowEngine ?? "",
            repoAndroid: settings.repositories?.android ?? "",
            repoIos: settings.repositories?.ios ?? ""
          });
          setHasExisting(true);
        } else {
          setForm(EMPTY_FORM);
          setHasExisting(false);
        }
      })
      .catch(() => {
        KMainFunctions.displayErrorNotification("Project settings could not be loaded.");
      })
      .finally(() => setIsLoading(false));
  }, [projectId, visible]);

  const setField = (
    field: Exclude<keyof ISourceRepositoryFormState, "provider" | "useAutoGeneratedBranch">,
    value: string
  ): void => {
    setForm((previous: ISourceRepositoryFormState) => ({ ...previous, [field]: value }));
  };

  const setUseAutoGeneratedBranch = (checked: boolean): void => {
    setForm((previous: ISourceRepositoryFormState) => ({ ...previous, useAutoGeneratedBranch: checked }));
  };

  const setProvider = (value: string): void => {
    const provider: SourceRepositoryProvider = value === "azureDevOps" ? "azureDevOps" : "github";
    setForm((previous: ISourceRepositoryFormState) => ({ ...previous, provider }));
  };

  const isGitHub: boolean = form.provider === "github";

  const validate = (): string | null => {
    if (isGitHub && !form.owner.trim()) return "GitHub owner (organization or user) is required.";
    if (!isGitHub && !form.url.trim()) return "Azure DevOps collection URL is required.";
    if (!isGitHub && !form.tfsProject.trim()) return "Azure DevOps project name is required.";
    if (!hasExisting && !form.token.trim()) return "Personal access token is required.";
    return null;
  };

  const handleSave = async (): Promise<void> => {
    // Save is clickable again the moment its ✓ replaces the spinner; a second click would re-send.
    if (!saveMachine.canStart) return;
    const validationError: string | null = validate();
    if (validationError) {
      KMainFunctions.displayErrorNotification(validationError);
      return;
    }

    const payload: ISourceRepositorySettings = {
      provider: form.provider,
      authType: "pat",
      url: form.url.trim() || null,
      owner: isGitHub ? form.owner.trim() : null,
      tfsProject: isGitHub ? null : form.tfsProject.trim(),
      user: form.user.trim() || null,
      // Empty/masked token means "keep the stored one" (write-only contract).
      token: form.token.trim() || TOKEN_MASK,
      branch: form.branch.trim() || null,
      useAutoGeneratedBranch: form.useAutoGeneratedBranch,
      repositories: {
        frontend: form.repoFrontend.trim() || null,
        backend: form.repoBackend.trim() || null,
        workflowEngine: form.repoWorkflowEngine.trim() || null,
        android: form.repoAndroid.trim() || null,
        ios: form.repoIos.trim() || null
      }
    };

    saveMachine.start();
    try {
      await ProjectSettingsService.upsertSourceRepository(projectId, payload);
      // The dialog stays open with the saved settings in it and Save's own ✓ confirms the write,
      // so there is no "saved" toast on top of it.
      setHasExisting(true);
      setForm((previous: ISourceRepositoryFormState) => ({ ...previous, token: "" }));
      saveMachine.succeed();
    } catch (error) {
      saveMachine.fail();
      // The server's own reason — the button can only say that it failed.
      KMainFunctions.displayErrorNotification(extractErrorMessage(error, "Settings could not be saved."));
    }
  };

  const handleDelete = async (): Promise<void> => {
    try {
      await ProjectSettingsService.deleteSourceRepository(projectId);
      KMainFunctions.displaySuccessNotification("Source repository settings removed.");
      setForm(EMPTY_FORM);
      setHasExisting(false);
    } catch (error) {
      KMainFunctions.displayErrorNotification(extractErrorMessage(error, "Settings could not be removed."));
    }
  };

  const repoPlaceholder = (suffix: string): string => `Default: kuika-${projectName}-${suffix}`;

  return (
    <>
      <DeleteModal
        itemName="Source Repository settings"
        triggerModal={isDeleteModalVisible}
        deleteItem={handleDelete}
        deleteType="Project Settings"
        closeModal={() => setIsDeleteModalVisible(false)}
      />
      <KModal
        className="kuika-project-settings-modal"
        showModal={visible}
        closeModal={onClose}
        width={880}
        header="Project Settings"
        footer={
          <KModalFooter
            onDelete={hasExisting ? () => setIsDeleteModalVisible(true) : undefined}
            deleteLabel="Remove"
            onCancel={onClose}
            submitLabel="Save"
            onSubmit={handleSave}
            status={saveStatus}
          />
        }
      >
        <div className="kuika-project-settings" data-testid="sourceRepositoryTab">
          {isFormWaiting ? (
            <KuikaSkeletonRegion
              label="Loading project settings"
              className={classNames("kuika-project-settings__skeleton", {
                "kuika-project-settings__skeleton--pending": presentation === "pending"
              })}
            >
              <KuikaSkeletonForm fields={6} />
            </KuikaSkeletonRegion>
          ) : (
            <div className="kuika-project-settings__section">
              {/* A lead line, not a callout: this is the dialog's own subject, and a coloured
                box would announce it as an exception to something. */}
              <KuikaSmallText className="kuika-project-settings__lead">
                Generated application source code is pushed to these repositories on deployment. Repositories are
                created automatically when they do not exist.
              </KuikaSmallText>

              <div className="kuika-project-settings__group">
                <KuikaSmallMediumText className="kuika-project-settings__group-title">Connection</KuikaSmallMediumText>
                <div className="kuika-project-settings__grid">
                  <div className="kuika-project-settings__field">
                    <KuikaSmallMediumText>Provider</KuikaSmallMediumText>
                    <KuikaSelectbox
                      options={PROVIDER_OPTIONS}
                      value={form.provider}
                      onChange={(value: string) => setProvider(value)}
                    />
                  </div>
                  {isGitHub ? (
                    <>
                      <div className="kuika-project-settings__field">
                        <KuikaSmallMediumText>Owner (organization or user)</KuikaSmallMediumText>
                        <KuikaInput
                          value={form.owner}
                          placeholder="acme-org"
                          onChange={(_e, value: string | number) => setField("owner", String(value))}
                        />
                      </div>
                      <div className="kuika-project-settings__field kuika-project-settings__field--wide">
                        <KuikaSmallMediumText>API base URL (optional, GitHub Enterprise)</KuikaSmallMediumText>
                        <KuikaInput
                          value={form.url}
                          placeholder="https://api.github.com"
                          onChange={(_e, value: string | number) => setField("url", String(value))}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="kuika-project-settings__field">
                        <KuikaSmallMediumText>Collection URL</KuikaSmallMediumText>
                        <KuikaInput
                          value={form.url}
                          placeholder="https://dev.azure.com/acme"
                          onChange={(_e, value: string | number) => setField("url", String(value))}
                        />
                      </div>
                      <div className="kuika-project-settings__field">
                        <KuikaSmallMediumText>Project</KuikaSmallMediumText>
                        <KuikaInput
                          value={form.tfsProject}
                          placeholder="DevOps project name"
                          onChange={(_e, value: string | number) => setField("tfsProject", String(value))}
                        />
                      </div>
                    </>
                  )}
                  <div className="kuika-project-settings__field">
                    <KuikaSmallMediumText>Username</KuikaSmallMediumText>
                    <KuikaInput
                      value={form.user}
                      placeholder="Committer / auth user"
                      onChange={(_e, value: string | number) => setField("user", String(value))}
                    />
                  </div>
                  <div className="kuika-project-settings__field">
                    <KuikaSmallMediumText>Personal access token</KuikaSmallMediumText>
                    <KuikaInput
                      type="password"
                      value={form.token}
                      placeholder={hasExisting ? "Saved — leave empty to keep the current token" : "Token"}
                      onChange={(_e, value: string | number) => setField("token", String(value))}
                    />
                  </div>
                </div>
              </div>

              <div className="kuika-project-settings__group">
                <KuikaSmallMediumText className="kuika-project-settings__group-title">Branch</KuikaSmallMediumText>
                <div className="kuika-project-settings__grid">
                  <div className="kuika-project-settings__field">
                    <KuikaSmallMediumText>
                      {form.useAutoGeneratedBranch ? "Base branch" : "Branch"}
                    </KuikaSmallMediumText>
                    <KuikaInput
                      value={form.branch}
                      placeholder="main"
                      onChange={(_e, value: string | number) => setField("branch", String(value))}
                    />
                  </div>
                  <div className="kuika-project-settings__field kuika-project-settings__field--wide">
                    <KuikaCheckbox
                      label="Push each publish to a new auto-generated branch"
                      checked={form.useAutoGeneratedBranch}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setUseAutoGeneratedBranch(event.target.checked)
                      }
                    />
                    {form.useAutoGeneratedBranch && (
                      <KuikaSmallText className="kuika-project-settings__hint">
                        Each publish creates a branch named kuika-gen-&#123;timestamp&#125; from the base branch; all
                        repositories of one publish share the same branch name.
                      </KuikaSmallText>
                    )}
                  </div>
                </div>
              </div>

              <div className="kuika-project-settings__group">
                <KuikaSmallMediumText className="kuika-project-settings__group-title">
                  Repository names
                </KuikaSmallMediumText>
                <KuikaSmallText className="kuika-project-settings__hint">
                  Optional. Leave a field empty to use the default derived from the project name.
                </KuikaSmallText>
                <div className="kuika-project-settings__grid">
                  <div className="kuika-project-settings__field">
                    <KuikaSmallMediumText>Frontend</KuikaSmallMediumText>
                    <KuikaInput
                      value={form.repoFrontend}
                      placeholder={repoPlaceholder("ui")}
                      onChange={(_e, value: string | number) => setField("repoFrontend", String(value))}
                    />
                  </div>
                  <div className="kuika-project-settings__field">
                    <KuikaSmallMediumText>Backend</KuikaSmallMediumText>
                    <KuikaInput
                      value={form.repoBackend}
                      placeholder={repoPlaceholder("backend")}
                      onChange={(_e, value: string | number) => setField("repoBackend", String(value))}
                    />
                  </div>
                  <div className="kuika-project-settings__field">
                    <KuikaSmallMediumText>Workflow engine</KuikaSmallMediumText>
                    <KuikaInput
                      value={form.repoWorkflowEngine}
                      placeholder={repoPlaceholder("wfe")}
                      onChange={(_e, value: string | number) => setField("repoWorkflowEngine", String(value))}
                    />
                  </div>
                  <div className="kuika-project-settings__field">
                    <KuikaSmallMediumText>Android</KuikaSmallMediumText>
                    <KuikaInput
                      value={form.repoAndroid}
                      placeholder={repoPlaceholder("android")}
                      onChange={(_e, value: string | number) => setField("repoAndroid", String(value))}
                    />
                  </div>
                  <div className="kuika-project-settings__field">
                    <KuikaSmallMediumText>iOS</KuikaSmallMediumText>
                    <KuikaInput
                      value={form.repoIos}
                      placeholder={repoPlaceholder("ios")}
                      onChange={(_e, value: string | number) => setField("repoIos", String(value))}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </KModal>
    </>
  );
};

const projectSettingsContainer = withSD<IProjectSettingsContainerProps>(ProjectSettingsContainerInner);
export { projectSettingsContainer as ProjectSettingsContainer };
